/**
 * Meeting times (TRUS-691, TRUS-695): entered and shown in the viewer's
 * SELECTED time zone (Settings > Preferences; else the org's, else this
 * device's), with the zone abbreviation, plus the org's time when it differs.
 * The schedule dialog's date / time inputs are wall-clock time in that zone and
 * are sent as UTC ISO strings.
 */

import { parseCallTime } from '@/services/callsApi';
import { browserTimeZone, toZonedInputs, zonedInputsToMs } from '@/utils/zonedTime';

/** This device's IANA time zone (callers pass the user's selected zone when they have it). */
export function viewerTimeZone(): string {
  return browserTimeZone();
}

function safeFormat(ms: number, locale: string | undefined, opts: Intl.DateTimeFormatOptions): string {
  try {
    return new Intl.DateTimeFormat(locale, opts).format(new Date(ms));
  } catch {
    return new Date(ms).toISOString();
  }
}

/** "Tue, Oct 21, 3:00 PM – 3:30 PM PDT" in `timeZone` (the viewer's selected zone), with its abbreviation. */
export function formatMeetingRange(startIso: string, endIso: string | null, locale?: string, timeZone?: string): string {
  const start = parseCallTime(startIso);
  if (start == null) return '';
  const end = parseCallTime(endIso);
  const tz = timeZone ? { timeZone } : {};
  const day = safeFormat(start, locale, { weekday: 'short', month: 'short', day: 'numeric', ...tz });
  const from = safeFormat(start, locale, {
    hour: 'numeric',
    minute: '2-digit',
    ...(end == null ? { timeZoneName: 'short' as const } : {}),
    ...tz,
  });
  const to =
    end != null ? safeFormat(end, locale, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short', ...tz }) : '';
  return to ? `${day}, ${from} – ${to}` : `${day}, ${from}`;
}

/** The same start time in the org's zone ("6:00 PM EDT"), or null when it is the viewer's zone / unknown. */
export function orgTimeNote(
  startIso: string,
  orgTimeZone: string | null | undefined,
  locale?: string,
  viewerZone?: string,
): string | null {
  const mine = viewerZone || viewerTimeZone();
  if (!orgTimeZone || orgTimeZone === mine) return null;
  const start = parseCallTime(startIso);
  if (start == null) return null;
  try {
    // Throws for an unknown zone.
    new Intl.DateTimeFormat(locale, { timeZone: orgTimeZone });
  } catch {
    return null;
  }
  const a = safeFormat(start, locale, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short', timeZone: mine });
  const b = safeFormat(start, locale, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short', timeZone: orgTimeZone });
  return a === b ? null : b;
}

/** `YYYY-MM-DD` and `HH:mm` for <input type=date|time>, in `timeZone` (default: this device's). */
export function toLocalInputs(ms: number, timeZone?: string): { date: string; time: string } {
  return toZonedInputs(ms, timeZone || viewerTimeZone());
}

/** Date + time inputs (wall-clock in `timeZone`) -> epoch ms, or null when either is missing / invalid. */
export function fromLocalInputs(date: string, time: string, timeZone?: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '')) return null;
  return zonedInputsToMs(date, time, timeZone || viewerTimeZone());
}

/** The next half hour from now (the dialog's default start). */
export function nextHalfHour(now = Date.now()): number {
  const step = 30 * 60_000;
  return Math.ceil((now + 60_000) / step) * step;
}

/** Minutes until a time ("in 5 min"), or null when it is past / unknown. */
export function minutesUntil(iso: string | null | undefined, now = Date.now()): number | null {
  const t = parseCallTime(iso);
  if (t == null || t <= now) return null;
  return Math.ceil((t - now) / 60_000);
}
